import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, newId } from '../api.js';
import { useMe } from '../App.jsx';
import { BackIcon, CheckIcon, ErrorBox, JobCard, Spinner } from '../components.jsx';

const MESSAGES = {
  name_required: 'Please type a name for the job (at least 3 letters).',
  network: 'No signal – nothing was created. Try again when you have signal.',
  not_configured: "Creating jobs isn't switched on yet. Tell the office.",
};

function Row({ ok, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white ${ok ? 'bg-green-600' : 'bg-amber !text-charcoal'}`}>
        {ok ? <CheckIcon className="h-4 w-4" /> : '!'}
      </span>
      <span>{children}</span>
    </li>
  );
}

function Created({ result, retry, busy, staff }) {
  const { job } = result;
  return (
    <div className="space-y-6">
      <div className="card p-5 text-center">
        <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-green-600 text-white"><CheckIcon className="h-10 w-10" /></div>
        <h1 className="text-2xl font-bold">Job created</h1>
        {job.jobNumber && <span className="badge mt-2 bg-amber text-charcoal">{job.jobNumber}</span>}
        <p className="mt-2 text-lg font-medium leading-snug">{job.name}</p>
      </div>

      <ul className="space-y-3 text-sm">
        <Row ok={result.folderReady}>
          {result.folderReady ? 'Its NextCloud folder is ready for photos.' : "Its NextCloud folder isn't ready yet, so photos can't be added until it is. Tell the office."}
        </Row>
        {result.ticket ? (
          <Row ok>A follow-up ticket (#{result.ticket.number}) is on your to-do list to fill in the customer details.</Row>
        ) : result.ticketFailed ? (
          <Row ok={false}>
            The job exists, but the follow-up ticket couldn't be created.{' '}
            <button className="font-bold underline" disabled={busy} onClick={retry}>{busy ? 'Trying…' : 'Try again'}</button>
          </Row>
        ) : null}
        {!result.assigned && <Row ok={false}>It couldn't be assigned to you automatically – assign it to yourself in the CRM.</Row>}
      </ul>

      <div className="space-y-3">
        <Link to={`/job/${job.id}`} className="btn-primary w-full">Add photos, notes or contacts</Link>
        <Link to="/" className="btn-ghost w-full">Back to search</Link>
      </div>
    </div>
  );
}

export default function NewJob() {
  const me = useMe();
  const [sid] = useState(newId);
  const [name, setName] = useState('');
  const [similar, setSimilar] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  // Staff only (the server enforces this too).
  useEffect(() => {
    const term = name.trim();
    setConfirmed(false);
    if (term.length < 3) { setSimilar([]); return; }
    const t = setTimeout(() => {
      api.get(`/api/jobs/search?q=${encodeURIComponent(term.split(/\s+/).slice(0, 2).join(' '))}`)
        .then((r) => setSimilar(r.jobs.slice(0, 5)))
        .catch(() => setSimilar([]));
    }, 400);
    return () => clearTimeout(t);
  }, [name]);

  async function create() {
    setBusy(true);
    setError(null);
    try {
      setResult(await api.post('/api/jobs', { sid, name: name.trim() }));
    } catch (e) {
      if (e.message === 'auth_required') { /* redirected to sign in */ }
      else if (e.data?.error === 'crm_rejected') setError(`The CRM wouldn't accept the new job (it needs a value for "${e.data.field}"). Tell the office.`);
      else setError(MESSAGES[e.data?.error] || MESSAGES[e.message] || "Couldn't create the job. Please try again.");
    }
    setBusy(false);
  }

  if (!me.canCreateJobs) {
    return <div className="space-y-4"><ErrorBox>You don't have permission to create jobs.</ErrorBox><Link to="/" className="btn-ghost w-full">Back</Link></div>;
  }
  if (result) return <Created result={result} retry={create} busy={busy} />;

  const needsConfirm = similar.length > 0 && !confirmed;

  return (
    <div className="space-y-5">
      <Link to="/" className="inline-flex items-center gap-1 py-1 text-steel"><BackIcon className="h-5 w-5" /> All jobs</Link>
      <div>
        <h1 className="text-2xl font-bold">Create a new job</h1>
        <p className="text-steel">A placeholder you can add photos and notes to straight away. A ticket reminds you to fill in the customer details later.</p>
      </div>

      <div>
        <label className="label" htmlFor="jobname">Job name</label>
        <input
          id="jobname" className="input !text-lg" autoComplete="off" autoCapitalize="sentences" maxLength={120}
          placeholder="Customer – what needs doing" value={name} onChange={(e) => setName(e.target.value)}
        />
        <p className="mt-1.5 text-xs text-steel">e.g. JBS Corowa – Coves for filleting room</p>
      </div>

      {similar.length > 0 && (
        <div className="space-y-3">
          <h2 className="label !mb-0">Already in the system? Tap to open instead</h2>
          {similar.map((j) => <JobCard key={j.id} job={j} to={`/job/${j.id}`} staff />)}
        </div>
      )}

      {error && <ErrorBox>{error}</ErrorBox>}

      {needsConfirm ? (
        <button className="btn-ghost w-full" disabled={name.trim().length < 3} onClick={() => setConfirmed(true)}>None of these – create a new job</button>
      ) : (
        <button className="btn-primary w-full" disabled={busy || name.trim().length < 3} onClick={create}>
          {busy ? <><Spinner className="h-5 w-5" /> Creating…</> : 'Create job'}
        </button>
      )}
    </div>
  );
}
